import { useConsent } from '@/contexts/ConsentContext'
import { useEffect, useState } from 'react'
import { useLocalStorage as useLocalStorageBase } from 'usehooks-ts'

/**
 * Hook for localStorage that respects functionality storage consent
 * @param key - The localStorage key
 * @param initialValue - Initial value if key doesn't exist or consent is denied
 * @returns [value, setValue] - Similar to useState but respects consent
 */
export function useLocalStorage<T>(
  ...args: Parameters<typeof useLocalStorageBase<T>>
): [T, (value: T) => void] {
  const { functionalityStorageAllowed } = useConsent()
  const [inMemoryValue, setInMemoryValue] = useState<T>(args[1])

  // Use the base localStorage hook but only when consent is granted
  const [persistedValue, setPersistedValue] = useLocalStorageBase<T>(...args)

  // Determine which value to use based on consent
  const currentValue = functionalityStorageAllowed
    ? persistedValue
    : inMemoryValue

  // Clear localStorage when consent is revoked
  useEffect(() => {
    if (!functionalityStorageAllowed && typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(args[0])
      } catch (error) {
        console.warn(`Error removing localStorage key "${args[0]}":`, error)
      }
    }
  }, [functionalityStorageAllowed, args])

  const setValue = (value: T) => {
    if (functionalityStorageAllowed) {
      // Store in localStorage when consent is granted
      setPersistedValue(value)
    } else {
      // Store only in memory when consent is denied
      setInMemoryValue(value)
    }
  }

  return [currentValue, setValue]
}
